import FundSection from './FundSection'
import { useState } from 'react'
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom'
import { useAuthSession } from '../../lib/authSession'
import { formatOfficerDate, formatOfficerDateTime, humanizeToken } from '../../lib/officerFormat'
import DocumentLink from '../documents/DocumentLink'
import { ErrorState, Skeleton } from '../state'
import { recordHref, type ViewScope } from './context'
import { useRead } from './useRead'
import {
  HISTORY_CATEGORIES,
  historyActor,
  historyDetailUrl,
  historyHref,
  historyIdentity,
  historyTitle,
  historyOutcome,
  historyValue,
  type HistoryEntry,
  type HistoryPage,
  type HistorySource,
} from './fundHistory'
import { historyDateRows } from './historyDates'
import '../fund/fundtabs.css'
import './history.css'

function selectedSource(params: URLSearchParams): HistorySource | null {
  const kind = params.get('entry_kind'),
    id = params.get('entry_id'),
    phase = params.get('entry_phase')
  if (
    !kind ||
    !id ||
    !phase ||
    ![
      'record_event',
      'evidence_decision',
      'record_edit',
      'close_check',
      'document_receipt',
    ].includes(kind) ||
    !/^[a-z][a-z0-9_]*$/.test(phase)
  )
    return null
  return { kind: kind as HistorySource['kind'], id, phase }
}
function sourceParams(params: URLSearchParams, source: HistorySource | null) {
  const next = new URLSearchParams(params)
  for (const key of ['entry_kind', 'entry_id', 'entry_phase']) next.delete(key)
  if (source) {
    next.set('entry_kind', source.kind)
    next.set('entry_id', source.id)
    next.set('entry_phase', source.phase)
  }
  return next
}
function CoverageNote() {
  return (
    <p className="hist-coverage" data-testid="history-coverage">
      Only retained history is shown. Some earlier activity may be unavailable.
    </p>
  )
}
function EntryLine({ entry }: { entry: HistoryEntry }) {
  return (
    <>
      <time dateTime={entry.occurred_at}>{formatOfficerDateTime(entry.occurred_at)}</time>
      <span>
        <strong>{historyTitle(entry)}</strong>
        <span className="hist-subject">
          {entry.content.subject.label ?? humanizeToken(entry.content.subject.type)}
        </span>
        <small>{historyActor(entry)}</small>
        {entry.content.preview ? (
          <small>Open details for the full retained information.</small>
        ) : null}
      </span>
    </>
  )
}
function HistoryDetails({
  scope,
  source,
  onClose,
}: {
  scope: ViewScope
  source: HistorySource
  onClose: () => void
}) {
  const [retry, setRetry] = useState(0)
  const location = useLocation()
  const read = useRead<HistoryEntry>(historyDetailUrl(scope.fund_id!, source), retry)
  if (read.error)
    return (
      <ErrorState
        what="history details"
        error={read.error}
        onRetry={() => setRetry((n) => n + 1)}
        compact
      />
    )
  if (!read.value) return <Skeleton kind="lines" n={3} />
  const entry = read.value
  return (
    <section className="hist-detail" aria-label="History details" data-testid="history-details">
      <div className="hist-detail-head">
        <strong>{historyTitle(entry)}</strong>
        <button type="button" className="nav-link" onClick={onClose}>
          Close details
        </button>
      </div>
      <p>
        Recorded {formatOfficerDateTime(entry.occurred_at)} · {historyActor(entry)}
      </p>
      <p>Result: {historyOutcome(entry)}</p>
      {historyDateRows(entry.financial_date, entry.content.dates, entry.content.subject.type).map(
        (row) => (
          <p key={row.key}>
            {row.label}
            {row.key === 'financial_date' ? '' : ':'} <b>{formatOfficerDate(row.value)}</b>
            {row.recordLink ? (
              <>
                {' · '}
                <Link
                  to={recordHref(scope, { asOf: row.value })}
                  aria-label={`Open Record on ${formatOfficerDate(row.value)}`}
                >
                  Open Record →
                </Link>
              </>
            ) : null}
          </p>
        )
      )}
      {entry.content.reason ? (
        <p className="hist-reason">
          <b>Reason</b> {entry.content.reason}
        </p>
      ) : null}
      {entry.content.changes.length ? (
        <div className="hist-table-wrap">
          <table className="hist-changes">
            <thead>
              <tr>
                <th>Field</th>
                <th>Before</th>
                <th>Saved / proposed value</th>
              </tr>
            </thead>
            <tbody>
              {entry.content.changes.map((change) => (
                <tr key={change.field}>
                  <th>{change.label}</th>
                  <td>{historyValue(change.before)}</td>
                  <td>
                    {historyValue(change.after)}
                    <small>
                      {change.value_status === 'proposed' ? 'Proposed, not saved' : 'Saved'}
                    </small>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {entry.content.checks.length ? (
        <div className="hist-checks">
          {entry.content.checks.map((check, index) => (
            <div key={`${check.id}:${index}`}>
              <strong>{check.label ?? humanizeToken(check.id)}</strong>
              <span>
                {humanizeToken(check.result)}
                {check.required ? ' · required' : ''}
              </span>
              {check.reason ? (
                <p>
                  <b>Recorded reason</b> {check.reason}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      {entry.content.documents.length ? (
        <div className="hist-documents">
          <b>Source documents</b>
          {entry.content.documents.map((document) =>
            document.available ? (
              <DocumentLink
                key={document.document_id}
                document={{
                  document_id: document.document_id,
                  display_title: document.title,
                  page: document.page,
                }}
                context={{ fundId: scope.fund_id, returnTo: location.pathname + location.search }}
              />
            ) : (
              <span key={document.document_id} className="hist-unavailable">
                {document.title ?? 'Original document'} · unavailable
              </span>
            )
          )}
        </div>
      ) : null}
    </section>
  )
}

export function FundHistoryPanel({ scope }: { scope: ViewScope }) {
  const [params, setParams] = useSearchParams()
  const session = useAuthSession()
  const [retry, setRetry] = useState(0)
  const category = params.get('category') ?? ''
  const selected = selectedSource(params)
  const key = JSON.stringify([scope.fund_id, category, session])
  const [paging, setPaging] = useState<{
    key: string
    cursor: string | null
    previous: HistoryEntry[]
  }>({ key: '', cursor: null, previous: [] })
  const current = paging.key === key ? paging : { key, cursor: null, previous: [] }
  const query = new URLSearchParams({ limit: '50' })
  if (category) query.set('category', category)
  if (current.cursor) query.set('cursor', current.cursor)
  const read = useRead<HistoryPage>(
    `/api/funds/${encodeURIComponent(scope.fund_id!)}/history?${query}`,
    retry
  )
  const items = [...current.previous, ...(read.value?.items ?? [])]
  const selectedVisible =
    selected && items.some((entry) => historyIdentity(entry.source) === historyIdentity(selected))
  const choose = (source: HistorySource | null) => setParams(sourceParams(params, source))
  return (
    <div className="hist-panel" data-testid="fund-history-panel">
      <div className="hist-toolbar">
        <div className="hist-categories" aria-label="History categories">
          {HISTORY_CATEGORIES.map((item) => (
            <button
              type="button"
              key={item.value}
              className={category === item.value ? 'on' : ''}
              aria-pressed={category === item.value}
              onClick={() => {
                const next = sourceParams(params, null)
                if (item.value) next.set('category', item.value)
                else next.delete('category')
                setParams(next)
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="nav-link"
          onClick={() => {
            setPaging({ key, cursor: null, previous: [] })
            setRetry((n) => n + 1)
          }}
        >
          Refresh
        </button>
      </div>
      <CoverageNote />
      {selected && !selectedVisible ? (
        <HistoryDetails scope={scope} source={selected} onClose={() => choose(null)} />
      ) : null}
      {items.length ? (
        <ol className="hist-list">
          {items.map((entry) => {
            const open = selected && historyIdentity(selected) === historyIdentity(entry.source)
            return (
              <li key={historyIdentity(entry.source)} data-testid="fund-history-entry">
                <button
                  type="button"
                  className="hist-entry"
                  aria-expanded={!!open}
                  onClick={() => choose(open ? null : entry.source)}
                >
                  <EntryLine entry={entry} />
                  <span aria-hidden="true">{open ? '−' : '+'}</span>
                </button>
                {open ? (
                  <HistoryDetails
                    scope={scope}
                    source={entry.source}
                    onClose={() => choose(null)}
                  />
                ) : null}
              </li>
            )
          })}
        </ol>
      ) : null}
      {read.loading ? (
        <Skeleton kind="rows" n={current.previous.length ? 2 : 4} height={66} />
      ) : null}
      {read.error ? (
        <ErrorState
          what="history"
          error={read.error}
          onRetry={() => setRetry((n) => n + 1)}
          testId="history-error"
        />
      ) : null}
      {!read.loading && !read.error && items.length === 0 ? (
        <p className="hist-empty" data-testid="history-empty">
          No retained events {category ? 'in this category' : 'for this fund'}.
        </p>
      ) : null}
      {read.value?.next_cursor && !read.loading ? (
        <button
          type="button"
          className="vbtn hist-more"
          onClick={() => setPaging({ key, cursor: read.value!.next_cursor, previous: items })}
        >
          Load older events
        </button>
      ) : null}
    </div>
  )
}

export function FundLatestActivity({
  scope,
  refresh = 0,
  collapsible = false,
}: {
  scope: ViewScope
  refresh?: number
  collapsible?: boolean
}) {
  const [retry, setRetry] = useState(0)
  const current = useRead<HistoryPage>(
    `/api/funds/${encodeURIComponent(scope.fund_id!)}/history?limit=5`,
    retry + refresh
  )
  return (
    <FundSection
      title="Latest activity"
      collapsible={collapsible}
      className="hist-latest"
      testId="fund-latest-activity"
      attention={
        current.error
          ? 'Latest activity unavailable'
          : current.loading
            ? 'Loading latest activity…'
            : null
      }
      actions={
        <Link className="right" to={historyHref(scope)}>
          History →
        </Link>
      }
    >
      {current.loading ? <Skeleton kind="rows" n={3} height={56} /> : null}
      {current.error ? (
        <ErrorState
          what="latest activity"
          error={current.error}
          onRetry={() => setRetry((n) => n + 1)}
          compact
        />
      ) : null}
      {current.value?.items.length ? (
        <ol>
          {current.value.items.map((entry) => (
            <li key={historyIdentity(entry.source)}>
              <Link className="hist-entry" to={historyHref(scope, entry.source)}>
                <EntryLine entry={entry} />
              </Link>
            </li>
          ))}
        </ol>
      ) : null}
      {current.value?.items.length === 0 ? (
        <p className="hist-empty">No retained events for this fund.</p>
      ) : null}
      <CoverageNote />
    </FundSection>
  )
}

export default function FundHistory() {
  const { id } = useParams<{ id: string }>()
  const [params] = useSearchParams()
  const fund = useRead<{ id: string; name: string }>(
    id ? `/api/funds/${encodeURIComponent(id)}` : null
  )
  if (!id) return <main className="detail">Fund context unavailable.</main>
  const scope: ViewScope = {
    kind: 'fund',
    fund_id: fund.value?.id ?? id,
    client_id: params.get('client'),
  }
  return (
    <main className="cockpit fa-page">
      <div className="fa" data-testid="fund-history-page">
        <header className="fa-head hist-head">
          <nav className="nav-context-links">
            <Link
              to={`/v2/funds/${encodeURIComponent(id)}${scope.client_id ? '?client=' + encodeURIComponent(scope.client_id) : ''}`}
            >
              ‹ Fund overview
            </Link>
            <Link to={recordHref(scope)}>Record</Link>
          </nav>
          <h1>History{fund.value?.name ? ` · ${fund.value.name}` : ''}</h1>
        </header>
        {fund.error ? <ErrorState what="fund" error={fund.error} /> : null}
        <section className="fa-card">
          <FundHistoryPanel key={scope.fund_id + ':' + scope.client_id} scope={scope} />
        </section>
      </div>
    </main>
  )
}
